import Deferred from '@webgl/core/Deferred'

import { reportError } from '@/instrumentation'

import { Resource } from './Resource'

const _domParser = new DOMParser()

const MAX_RETRIES = 3
const REQUEST_TIMEOUT_MS = 30000

class HttpError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message)
    this.name = 'HttpError'
  }
}

function validateResponse(response: Response): void {
  if (!response.ok) {
    throw new HttpError(
      response.status,
      `NetworkError: status : ${response.status} ${response.statusText}, url : ${response.url}`
    )
  }
}

// Resolves when `ms` elapses, or early if the signal aborts.
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve()
    const onAbort = () => {
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

// Retry transient failures (network errors, timeouts, 5xx); 4xx are permanent.
function isRetryable(error: unknown): boolean {
  if (error instanceof HttpError && error.status >= 400 && error.status < 500) return false
  return true
}

/**
 * fetch equivalent which rejects if status is not OK, with a per-attempt timeout and bounded
 * retries (exponential backoff) for transient failures.
 *
 * The Promise returned from fetch() won’t reject on HTTP error status even if the response is an HTTP 404 or 500.
 * Instead, it will resolve normally (with ok status set to false), and it will only reject on network failure or
 * if anything prevented the request from completing.
 */
export async function fetchSafe(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const callerSignal = init?.signal ?? undefined
  let lastError: unknown
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const onCallerAbort = () => controller.abort(callerSignal?.reason)
    const timer = setTimeout(
      () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
      REQUEST_TIMEOUT_MS
    )
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort(callerSignal.reason)
      else callerSignal.addEventListener('abort', onCallerAbort, { once: true })
    }
    try {
      const response = await fetch(input, { ...init, signal: controller.signal })
      validateResponse(response)
      return response
    } catch (error) {
      lastError = error
      // Caller-initiated abort must not be retried or swallowed.
      if (callerSignal?.aborted || !isRetryable(error) || attempt === MAX_RETRIES) break
      await delay(2 ** attempt * 1000, callerSignal)
    } finally {
      clearTimeout(timer)
      callerSignal?.removeEventListener('abort', onCallerAbort)
    }
  }
  // Report genuine failures, but not expected caller-initiated cancellations.
  // reportError additionally filters abort-shaped errors and attaches the context.
  if (!callerSignal?.aborted) {
    reportError(lastError, { url: typeof input === 'string' ? input : input.url })
  }
  throw lastError
}

abstract class AbstractFetchResource<T = unknown> extends Resource<T> {
  readonly url: RequestInfo
  readonly init: RequestInit

  constructor(url: RequestInfo, init: RequestInit = {}) {
    super()
    this.url = url
    this.init = init
  }

  async doLoad(): Promise<T> {
    const init = Object.assign({}, this.init, { signal: this.abortSignal })
    const response = await fetchSafe(this.url, init)
    return this.parseResponse(response)
  }

  doUnload(): void {}

  abstract parseResponse(response: Response): Promise<T>
}

export class FetchResource extends AbstractFetchResource<Response> {
  parseResponse(response: Response): Promise<Response> {
    return Promise.resolve(response)
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class JsonResource extends AbstractFetchResource<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parseResponse(response: Response): Promise<any> {
    return response.json()
  }
}

export class BlobResource extends AbstractFetchResource<Blob> {
  parseResponse(response: Response): Promise<Blob> {
    return response.blob()
  }
}

export class TextResource extends AbstractFetchResource<string> {
  parseResponse(response: Response): Promise<string> {
    return response.text()
  }
}

export class BytesResource extends AbstractFetchResource<ArrayBuffer> {
  parseResponse(response: Response): Promise<ArrayBuffer> {
    return response.arrayBuffer()
  }
}

export class XmlResource extends AbstractFetchResource<Document> {
  async parseResponse(response: Response): Promise<Document> {
    const t = await response.text()
    return _domParser.parseFromString(t, 'application/xml')
  }
}

export class ImageResource extends AbstractFetchResource<HTMLImageElement> {
  doUnload(): void {
    super.doUnload()

    const img = this.value
    if (img) {
      img.src = ''
    }
  }

  async parseResponse(response: Response): Promise<HTMLImageElement> {
    const blob = await response.blob()

    const src = URL.createObjectURL(blob)
    const img = new Image()

    const def = new Deferred<Event>()
    img.onload = def.resolve
    img.onerror = def.reject
    img.src = src

    try {
      await def.promise
    } finally {
      URL.revokeObjectURL(src)
    }

    await img.decode()
    return img
  }
}

export async function loadText(url: RequestInfo, init?: RequestInit): Promise<string> {
  return new TextResource(url, init).load()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function loadJson(url: RequestInfo, init?: RequestInit): Promise<any> {
  return new JsonResource(url, init).load()
}

export async function loadBytes(url: RequestInfo, init?: RequestInit): Promise<ArrayBuffer> {
  return new BytesResource(url, init).load()
}

export async function loadBlob(url: RequestInfo, init?: RequestInit): Promise<Blob> {
  return new BlobResource(url, init).load()
}

export async function loadXml(url: RequestInfo, init?: RequestInit): Promise<Document> {
  return new XmlResource(url, init).load()
}

export async function loadImage(url: RequestInfo, init?: RequestInit): Promise<HTMLImageElement> {
  return new ImageResource(url, init).load()
}
